import React from 'react';
import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
    colorToCssValue,
    createResponsiveStyles,
    getSizeValue,
    HoneycombTheme,
    mergeResponsiveStyleValues,
    ResponsiveStyleValue,
    SpacingValue,
    TabsColor,
    TabsSize,
    TabsVariant,
    useHoneycombTheme,
} from '../../theming';
import { alpha } from '../../utils';

export const STATIC_STYLES: SerializedStyles = css`
    /* Tabs root */
    position: relative;

    &:where([data-underline]) {
        &:before {
            content: '';
            position: absolute;
            bottom: 0;
            height: var(--tabs-underline-height);
            width: 100%;
            border-radius: var(--tabs-underline-radius);
            background-color: var(--hc-color-divider);
        }
    }

    /* Scroller track (flex container) */
    [data-scroller-track] {
        // background: pink;
        gap: var(--tabs-spacing) !important;
    }

    &:where([data-positioning='centered']) {
        [data-scroller-root] {
            // background: yellow;
            display: flex;
            justify-content: center;
        }
    }

    /* Tab list item */
    [data-tab-list-item] {
        display: flex;

        /* Buffer for active button underline */
        padding-bottom: var(--tabs-underline-height);

        /* Tab button */
        [data-tab-button] {
            flex: 1 1 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            align-items: center;
            position: relative;
            color: var(--tabs-color);
            opacity: 0.75;

            padding: var(--tabs-padding-y) var(--tabs-padding-x);
            font-family: var(--tabs-font-family);
            font-weight: var(--tabs-font-weight);
            font-size: var(--tabs-font-size);
            line-height: var(--tabs-line-height);
            text-transform: var(--tabs-text-transform);

            &:where([data-active]) {
                opacity: 1;

                &:after {
                    content: '';
                    width: 100%;
                    position: absolute;
                    inset-inline-start: 0;
                    bottom: calc(var(--tabs-underline-height) * -1);
                    height: var(--tabs-underline-height);
                    background-color: currentColor;
                    border-radius: var(--tabs-underline-radius);
                }
            }

            &:focus-visible {
                background: var(--tabs-focus-color);
            }
        }
    }

    /* Full width overrides */
    &:where([data-positioning='fullWidth']) {
        [data-scroller-root] {
            width: 100%;
        }

        [data-scroller-track] {
            gap: 0 !important;
        }

        [data-tab-list-item] {
            flex: 1 1 0;
        }
    }
`;

function getResponsiveStyles(theme: HoneycombTheme, variant: TabsVariant) {
    return ({ spacing, size }: { spacing: SpacingValue; size: TabsSize }) => {
        const componentTheme = theme.components.themes.Tabs;

        const variantProps = componentTheme.variant[variant];
        const sizeProps = variantProps.size[size];

        const typographyProps = theme.fn.getTypographyVariant(sizeProps.typographyVariant);

        return {
            '--tabs-spacing': theme.fn.getSpacingValue(spacing) ?? getSizeValue(sizeProps.spacing),
            '--tabs-underline-height': getSizeValue(variantProps.underlineHeight),
            '--tabs-underline-radius': variantProps.underlineRounded ? 'calc(var(--tabs-underline-height) / 2)' : '0',
            '--tabs-padding-x': getSizeValue(sizeProps.padding.x),
            '--tabs-padding-y': getSizeValue(sizeProps.padding.y),
            '--tabs-font-family': typographyProps.fontFamily,
            '--tabs-font-weight': typographyProps.fontWeight.default,
            '--tabs-font-size': typographyProps.fontSize,
            '--tabs-line-height': typographyProps.lineHeight,
            '--tabs-text-transform': typographyProps.textTransform,
        };
    };
}

export function useDynamicStyles(
    size: ResponsiveStyleValue<TabsSize>,
    variant: TabsVariant,
    color: TabsColor
): CSSObject {
    const { theme } = useHoneycombTheme();
    const variantProps = theme.components.themes.Tabs.variant[variant];
    const colorValue = color === 'default' ? variantProps.defaultColor : color;

    const styles = React.useMemo(() => {
        const focusColor = alpha(theme.fn.resolveColorValue(colorValue), 0.1);

        return {
            '--tabs-color': colorToCssValue(colorValue),
            '--tabs-focus-color': focusColor,
            ...createResponsiveStyles(
                [
                    {
                        value: mergeResponsiveStyleValues({ size }, theme),
                        getStyles: getResponsiveStyles(theme, variant),
                    },
                ],
                theme
            ),
        };
    }, [colorValue, size, theme, variant]);

    return styles;
}
